import { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Box, Typography, Button, CircularProgress, Dialog, IconButton } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import CloseIcon from '@mui/icons-material/Close';
import { useCatalogueReviews } from '../../hooks/useCatalogueReviews';
import { NewPagination } from '../../components/Common/Pagination/Pagination';

interface CatalogueReviewsProps {
  onItemCountChange?: (count: number) => void;
}

const CatalogueReviews = forwardRef<any, CatalogueReviewsProps>(({ onItemCountChange }, ref) => {
  const { 
    reviews, 
    loading, 
    approveReview, 
    declineReview, 
    bulkApproveReviews,
    pagination,
  } = useCatalogueReviews();
  const [processingReviewId, setProcessingReviewId] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  // Notify parent of item count changes
  useEffect(() => {
    if (onItemCountChange) {
      onItemCountChange(reviews.length);
    }
  }, [reviews.length, onItemCountChange]);

  const handleApprove = async (reviewId: string) => {
    setProcessingReviewId(reviewId);
    
    await approveReview(reviewId);
    
    setProcessingReviewId(null);
  };

  const handleDecline = async (reviewId: string) => {
    setProcessingReviewId(reviewId);
    
    await declineReview(reviewId);
    
    setProcessingReviewId(null);
  };

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    handleBulkApprove: bulkApproveReviews,
  }));

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-4-value)', flex: 1, overflow: 'hidden' }}>
      {/* Reviews List Container */}
      {reviews.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <Typography>No services pending approval</Typography>
        </Box>
      )  : (<Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '0', 
        flex: 1, 
        overflowY: 'auto',
        overflowX: 'hidden',
        paddingRight: 'var(--palette-light-4-value)', // 16px for scrollbar
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'var(--palette-light-text-tertiary-value-hex)',
          borderRadius: '4px',
        },
      }}>
        {reviews.map((review) => (
          <Box
            key={review.reviewId}
            sx={{
              display: 'flex',
              gap: 'var(--palette-light-10-value)', // 40px
              padding: 'var(--palette-light-6-value) 0', // 24px vertical padding
              borderBottom: '1px solid var(--palette-light-background-tertiary-value-hex)',
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-4-value)', flex: 1, minWidth: '250px' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-2-value)' }}>
                <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                  {formatDate(review.reviewCreatedAt)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 'var(--palette-light-1-value)' }}>
                  {[...Array(5)].map((_, i) => (
                    i < review.rating ? (
                      <StarIcon key={i} sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-brand-secondary-value-hex)' }} />
                    ) : (
                      <StarBorderIcon key={i} sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-tertiary-value-hex)' }} />
                    )
                  ))}
                  <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                    {review.rating}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-1-value)' }}>
                <Box sx={{ display: 'flex', gap: 'var(--palette-light-1-value)' }}>
                  <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                    {review.userName},
                  </Typography>
                  <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                    Order #{review.orderId}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 'var(--palette-light-1-value)' }}>
                  <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                    Vendor:
                  </Typography>
                  <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-brand-primary-value-hex)' }}>
                    {review.vendorName}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 'var(--palette-light-1-value)' }}>
                  <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                    Service:
                  </Typography>
                  <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                    {review.productName}
                  </Typography>
                </Box>
              </Box>
              {review.images && review.images.length > 0 && (
                <Box sx={{ display: 'flex', gap: 'var(--palette-light-2-value)', flexWrap: 'wrap' }}>
                  {review.images.map((image, imgIndex) => (
                    <Box
                      key={imgIndex}
                      component="img"
                      src={image}
                      alt={`Review image ${imgIndex + 1}`}
                      onClick={() => setSelectedImage(image)}
                      sx={{
                        width: 'var(--palette-light-20-value)', // 80px
                        height: 'var(--palette-light-20-value)', // 80px
                        borderRadius: 'var(--palette-light-3-value)', // 12px
                        objectFit: 'cover',
                        cursor: 'pointer',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': {
                          transform: 'scale(1.05)',
                          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                        },
                      }}
                      onError={(e: any) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ))}
                </Box>
              )}
              <Typography sx={{ 
                fontSize: 'var(--palette-light-font-size-default-value)', 
                color: 'var(--palette-light-text-primary-value-hex)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                wordBreak: 'break-word',
                lineHeight: '24px',
                maxHeight: '48px',
                width: '100%',
              }}>
                {review.reviewText}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 'var(--palette-light-2-value)', justifyContent: 'flex-end', alignItems: 'flex-start', minWidth: '300px' }}>
              <Button
                variant="contained"
                disabled={processingReviewId === review.reviewId}
                onClick={() => handleApprove(review.reviewId)}
                sx={{
                  height: 'var(--palette-light-14-value)', // 56px
                  padding: '0 var(--palette-light-8-value)', // 0 32px
                  borderRadius: 'var(--palette-light-4-value)', // 16px
                  backgroundColor: 'var(--palette-light-brand-primary-value-hex)',
                  color: 'var(--palette-light-text-foreground-value-hex)',
                  fontSize: 'var(--palette-light-font-size-default-value)',
                  fontWeight: 700,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'var(--palette-light-brand-primary-value-hex)',
                    opacity: 0.9,
                  },
                  '&:disabled': {
                    backgroundColor: 'var(--palette-light-text-tertiary-value-hex)',
                    color: 'var(--palette-light-text-foreground-value-hex)',
                  },
                }}
              >
                {processingReviewId === review.reviewId ? <CircularProgress size={20} color="inherit" /> : 'Approve'}
              </Button>
              <Button
                variant="outlined"
                disabled={processingReviewId === review.reviewId}
                onClick={() => handleDecline(review.reviewId)}
                sx={{
                  height: 'var(--palette-light-14-value)', // 56px
                  padding: '0 var(--palette-light-8-value)', // 0 32px
                  borderRadius: 'var(--palette-light-4-value)', // 16px
                  borderColor: 'var(--palette-light-brand-primary-value-hex)',
                  color: 'var(--palette-light-brand-primary-value-hex)',
                  fontSize: 'var(--palette-light-font-size-default-value)',
                  fontWeight: 700,
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: 'var(--palette-light-brand-primary-value-hex)',
                    backgroundColor: 'rgba(103, 49, 79, 0.04)',
                  },
                  '&:disabled': {
                    borderColor: 'var(--palette-light-text-tertiary-value-hex)',
                    color: 'var(--palette-light-text-tertiary-value-hex)',
                  },
                }}
              >
                {processingReviewId === review.reviewId ? <CircularProgress size={20} color="inherit" /> : 'Decline'}
              </Button>
            </Box>
          </Box>
        ))}
      </Box>)}

      <NewPagination
        {...pagination}
      />

      {/* Image Preview Modal */}
      <Dialog
        open={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        maxWidth="lg"
        PaperProps={{
          sx: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            overflow: 'hidden',
          },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            maxWidth: '90vw',
            maxHeight: '90vh',
          }}
        >
          <IconButton
            onClick={() => setSelectedImage(null)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              zIndex: 1,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          {selectedImage && (
            <Box
              component="img"
              src={selectedImage}
              alt="Review image preview"
              sx={{
                maxWidth: '100%',
                maxHeight: '90vh',
                objectFit: 'contain',
                borderRadius: 'var(--palette-light-2-value)',
              }}
              onError={(e: any) => {
                e.target.src = '';
                e.target.alt = 'Failed to load image';
              }}
            />
          )}
        </Box>
      </Dialog>
    </Box>
  );
});

CatalogueReviews.displayName = 'CatalogueReviews';

export default CatalogueReviews;
